import { Box, Button, Collapse, Grid, Pagination, Paper } from "@mui/material"
import { useSnackbar } from "notistack"
import React from "react"
import { ApiError } from "../../Api/ApiError"
import { addLessonReservable, editLessonReservable, getLessonReservableList, LessonReservables } from "../../Api/OnlineLesson"
import { Time } from "../../Helper/Time"
import { WeekType } from "../../Helper/Week"
import { LessonReservable } from "../../Objects/OnlineLesson"
import CategoryTitle from "../atoms/CategoryTitle"
import LessonReservableInputForm from "../molecules/LessonReservableInptForm"
import LessonReservableItem from "../molecules/LessonReservableItem"

export interface LessonReservableListProps {
    teacher_id: number
    display_rows: number
}
const LessonReservableList: React.FC<LessonReservableListProps> = ({
    teacher_id,
    display_rows,
}) => {
    let [lessonReservables, setLessonReservables] = React.useState<LessonReservables>()
    let [pagenum, setPagenum] = React.useState(1)

    let [formOpen, setFormOpen] = React.useState(false)
    let [addWeek, setAddWeek] = React.useState<WeekType>()
    let [addStartApply, setAddStartApply] = React.useState<Date>()
    let [addEndApply, setAddEndApply] = React.useState<Date>()
    let [addStartTime, setAddStartTime] = React.useState<Time>(new Time("00:00"))
    let [addEndTime, setAddEndTime] = React.useState<Time>(new Time("30:00"))
    let [editLrvableId, setEditLrvableId] = React.useState<number>()
    let { enqueueSnackbar } = useSnackbar()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    /**
     * 予約可能範囲設定データを取得
     */
    let loadData = React.useCallback((pagenum: number) => {
        getLessonReservableList({
            limit: display_rows,
            offset: (pagenum - 1) * display_rows,
            teacher_id: teacher_id
        }, {signal: abortController.signal}).then(lessonReservables => {
            setLessonReservables(lessonReservables)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, teacher_id, display_rows, enqueueSnackbar])

    /**
     * フォームデータの初期化
     */
    let initFormValues = React.useCallback(({
        lrvableId,
        week,
        startApply,
        endApply,
        startTime,
        endTime,
    }: {
        lrvableId?: number,
        week?: WeekType,
        startApply?: Date,
        endApply?: Date,
        startTime?: Time,
        endTime?: Time,
    }) => {
        setAddWeek(week)
        setAddStartApply(startApply)
        setAddEndApply(endApply)
        setAddStartTime(startTime ?? new Time("00:00"))
        setAddEndTime(endTime ?? new Time("30:00"))
        setEditLrvableId(lrvableId)
    }, [])

    const doEditLessonReservable = React.useCallback(() => {
        if( addWeek === undefined ) {
            enqueueSnackbar("曜日を選択してください", { variant: "error" })
            return;
        }
        if ( addStartTime === undefined ) {
            enqueueSnackbar("開始時間を選択してください", { variant: "error" })
            return;
        }
        if ( addEndTime === undefined ) {
            enqueueSnackbar("終了時間を選択してください", { variant: "error" })
            return;
        }
        if (editLrvableId) {
            editLessonReservable({
                lrvable_id: editLrvableId,
                teacher_id: teacher_id,
                week_conditions: addWeek,
                start: addStartTime,
                end: addEndTime,
                start_apply: addStartApply,
                end_apply: addEndApply
            }, {signal: abortController.signal}).then(lrvable_id => {
                enqueueSnackbar("変更しました", { variant: "success" })
                setFormOpen(false)
                loadData(pagenum)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        } else {
            addLessonReservable({
                teacher_id: teacher_id,
                week_conditions: addWeek,
                start: addStartTime,
                end: addEndTime,
                start_apply: addStartApply,
                end_apply: addEndApply
            }, {signal: abortController.signal}).then(lrvable_id => {
                enqueueSnackbar("登録しました", { variant: "success" })
                setFormOpen(false)
                loadData(pagenum)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, addWeek, editLrvableId, teacher_id, addStartTime, addEndTime, addStartApply, addEndApply, loadData, enqueueSnackbar, pagenum])

    /**
     * ページ変更時のデータ取得処理
     */
    React.useEffect(() => {
        loadData(pagenum)
    }, [pagenum, loadData])

    return (
        <Paper>
            <CategoryTitle text="予約可能範囲設定" />

            <Box
                sx={{
                    padding: '10px'
                }}
            >
                <Collapse
                    in={formOpen}
                    onExited={() => {
                        initFormValues({})
                    }}
                >
                    <LessonReservableInputForm
                        onChange={(week: WeekType | null | undefined, startApply: Date | null | undefined, endApply: Date | null | undefined, start: Time | null, end: Time | null) => {
                            setAddWeek(week ? week : undefined)
                            setAddStartApply(startApply ? startApply : undefined)
                            setAddEndApply(endApply ? endApply : undefined)
                            setAddStartTime(start ?? new Time("00:00"))
                            setAddEndTime(end ?? new Time("30:00"))
                        }}
                        lrvableId={editLrvableId}
                        week={addWeek}
                        startApply={addStartApply}
                        endApply={addEndApply}
                        startTime={addStartTime as Time}
                        endTime={addEndTime as Time}
                        timeRangeFrom={new Time("00:00")}
                        timeRangeTo={new Time("30:00")}
                        timeRangeUnit={30}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'right'
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setFormOpen(false)
                            }}
                            sx={{
                                margin: "0 10px"
                            }}
                        >キャンセル</Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                doEditLessonReservable()
                            }}
                            sx={{
                                margin: "0 10px"
                            }}
                        >{editLrvableId ? "変更" : "追加"}</Button>
                    </Box>
                </Collapse>
                <Collapse
                    in={!formOpen}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'right'
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setFormOpen(true)
                            }}
                        >追加</Button>
                    </Box>
                </Collapse>
            </Box>
            <Box sx={{
                padding: "10px"
            }}>
                <Grid container spacing={2} alignItems="stretch">
                    {lessonReservables &&
                        lessonReservables.list.map((lessonReservable: LessonReservable, index: number) => {
                            return (
                                <Grid item key={teacher_id + '-' + index} xs={12} sm={6} md={4}>
                                    <LessonReservableItem
                                        lessonReservable={lessonReservable}
                                        // onDelete={() => {
                                        // }}
                                        onEdit={() => {
                                            initFormValues({
                                                lrvableId: lessonReservable.lrvable_id,
                                                week: lessonReservable.week_conditions,
                                                startApply: lessonReservable.start_apply ? lessonReservable.start_apply : undefined,
                                                endApply: lessonReservable.end_apply ? lessonReservable.end_apply : undefined,
                                                startTime: lessonReservable.start,
                                                endTime: lessonReservable.end,
                                            })
                                            setFormOpen(true)
                                        }}
                                    />
                                </Grid>
                            )
                        })
                    }
                </Grid>
            </Box>
            {lessonReservables &&
                <Box sx={{
                    padding: "10px",
                    display: "flex",
                    justifyContent: "center",
                }}>
                    <Pagination
                        count={lessonReservables.pagenum}
                        onChange={(e, page) => {
                            setPagenum(page)
                            // onPageChange(page)
                        }}
                        variant="outlined"
                    />
                </Box>
            }
        </Paper>
    )
}


export default LessonReservableList